import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  ToolboxComponent,
  DataZoomInsideComponent,
  DataZoomComponent
} from 'echarts/components';

import { CandlestickChart, LineChart } from 'echarts/charts';
import timeStampToDate from '@/helpers/timeStampToDate';
import { Dropdown, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import numberHumanizer from '@/helpers/numberHumanizer';
import CustomLegent from '@/app/common/components/charts/CustomLegent';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import { useAppContext } from '@/Main';

echarts.use([
  LineChart,
  TooltipComponent,
  ToolboxComponent,
  DataZoomComponent,
  CandlestickChart,
  GridComponent,
  DataZoomInsideComponent,
  LegendComponent
]);

type ChartValue = number | string | null | undefined;
type ChartDate = number | string;

export type LineTimeChartSeries = {
  name: string;
  data: ChartValue[];
  color?: string;
  yAxisIndex?: number;
  axisFormatter?: (value: number | string) => string;
  inverse?: boolean;
  axisLabel?: string;
  itemStyle?: {
    color?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type LineTimeChartDateRange = {
  start_date: string;
  end_date: string;
};

export type LineTimeChartProps = {
  series?: LineTimeChartSeries[];
  dates?: ChartDate[];
  showName?: boolean;
  isInversed?: boolean;
  isFullDate?: boolean;
  showLegent?: boolean;
  topLabel?: string;
  hideControls?: boolean;
  dualYAxis?: boolean;
  independentYAxis?: boolean;
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  onTimeRangeChange?: (range: LineTimeChartDateRange) => void;
};

type TimeRangeValue =
  | 'week'
  | 'month'
  | '3months'
  | '6months'
  | 'year'
  | 'all';

type TimeRangeOption = {
  value: TimeRangeValue;
  label: string;
  days: number | null;
};

type ZoomState = {
  start?: number;
  end?: number;
};

type TooltipParam = {
  seriesName?: string;
  value?: ChartValue;
  color?: string;
  axisValue?: ChartValue;
};

const getSeriesAxisBounds = (data: ChartValue[]) => {
  const values = (data ?? []).map(Number).filter(value => !Number.isNaN(value));

  if (!values.length) {
    return {};
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) {
    const padding = Math.max(Math.abs(min) * 0.1, 1);
    return { min: min - padding, max: max + padding };
  }

  const padding = (max - min) * 0.08;
  return { min: min - padding, max: max + padding };
};

const formatRoundedAxisLabel = (value: number | string) => {
  const num = Number(value);

  if (Number.isNaN(num)) {
    return '';
  }

  if (Math.abs(num) >= 1000) {
    return `${Math.round(num / 1000)}k`.toPersianDigit();
  }

  return String(Math.round(num)).toPersianDigit();
};

const buildIndependentYAxes = (
  seriesList: LineTimeChartSeries[],
  isInversedDefault = false
) => {
  const hideAxisLabels = seriesList.length >= 3;

  return seriesList.map((item, index) => {
    const bounds = getSeriesAxisBounds(item.data);
    const isLeft = index % 2 === 0;
    const sideIndex = Math.floor(index / 2);
    const showAxisDetails = sideIndex === 0 && !hideAxisLabels;

    return {
      type: 'value',
      gridIndex: 0,
      position: isLeft ? 'left' : 'right',
      offset: sideIndex * 58,
      inverse: item.inverse ?? (isInversedDefault && index === 0),
      scale: false,
      ...bounds,
      name: showAxisDetails ? item.axisLabel || item.name || '' : '',
      axisLine: { show: false, onZero: false },
      splitLine: {
        show: index === 0,
        lineStyle: { color: getColor('gray-200') }
      },
      axisTick: { show: false },
      axisLabel: {
        show: showAxisDetails,
        color: getColor('gray-600'),
        formatter: (value: number | string) => {
          if (typeof item.axisFormatter === 'function') {
            return item.axisFormatter(value);
          }

          return formatRoundedAxisLabel(value);
        }
      },
      nameTextStyle: {
        color: getColor('gray-600'),
        fontFamily: 'dana'
      }
    };
  });
};

const LineTimeChart = ({
  series = [],
  dates = [],
  showName = false,
  isInversed = false,
  isFullDate = false,
  showLegent = false,
  topLabel = '',
  hideControls = false,
  dualYAxis = false,
  independentYAxis = false,
  leftAxisLabel = '',
  rightAxisLabel = '',
  onTimeRangeChange
}: LineTimeChartProps) => {
  const usesSingleGrid = dualYAxis || independentYAxis;
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const {
    config: { isDark }
  } = useAppContext() as { config: { isDark: boolean } };
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const isChartInitialized = useRef(false);
  const currentTimeRange = useRef<TimeRangeValue>('all');
  useTooltipHide(chartRef);

  const colorList = [
    getColor('navbar-vertical-default-link-active-color'),
    getColor('info'),
    getColor('gray-1100'),
    getColor('warning')
  ];

  const [isDotsAllowed, setIsDotsAllowed] = useState(false);
  const [isNumbersAllowed, setIsNumbersAllowed] = useState(false);
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<TimeRangeValue>('all');

  const dataZoomRef = useRef<ZoomState[] | null>(null);
  const legendStateRef = useRef<Record<string, boolean> | null>(null);

  // Time range options
  const timeRangeOptions: TimeRangeOption[] = [
    { value: 'week', label: '۱ هفته', days: 7 },
    { value: 'month', label: '۱ ماه', days: 30 },
    { value: '3months', label: '۳ ماه', days: 90 },
    { value: '6months', label: '۶ ماه', days: 180 },
    { value: 'year', label: '۱ سال', days: 365 },
    { value: 'all', label: 'همه', days: null }
  ];

  // Calculate time range start and end percentages
  const calculateTimeRange = useCallback(
    (range: TimeRangeValue) => {
      if (range === 'all' || !dates || dates.length === 0) {
        return { start: 0, end: 100 };
      }

      const totalDays = dates.length;
      const rangeDays = timeRangeOptions.find(
        option => option.value === range
      )?.days;

      if (!rangeDays || rangeDays >= totalDays) {
        return { start: 0, end: 100 };
      }

      const startPercentage = Math.max(
        0,
        ((totalDays - rangeDays) / totalDays) * 100
      );
      return { start: startPercentage, end: 100 };
    },
    [dates]
  );

  const getDateRangeFromSelection = useCallback(
    (range: TimeRangeValue): LineTimeChartDateRange | null => {
      if (!dates?.length) {
        return null;
      }

      const endTimestamp = dates[dates.length - 1];
      const end_date = dayjs.unix(Number(endTimestamp)).format('YYYY-MM-DD');

      if (range === 'all') {
        return {
          start_date: dayjs.unix(Number(dates[0])).format('YYYY-MM-DD'),
          end_date
        };
      }

      const rangeDays = timeRangeOptions.find(
        option => option.value === range
      )?.days;

      if (!rangeDays) {
        return null;
      }

      const startIndex = Math.max(0, dates.length - rangeDays);

      return {
        start_date: dayjs.unix(Number(dates[startIndex])).format('YYYY-MM-DD'),
        end_date
      };
    },
    [dates]
  );

  // Handle time range change without triggering re-render
  const handleTimeRangeChange = useCallback(
    (range: TimeRangeValue) => {
      currentTimeRange.current = range;
      setSelectedTimeRange(range);
      const selectedRange = getDateRangeFromSelection(range);
      if (selectedRange) {
        onTimeRangeChange?.(selectedRange);
      }

      if (chartRef.current) {
        const chartInstance = chartRef.current.getEchartsInstance();
        const { start, end } = calculateTimeRange(range);

        // Apply the time range to dataZoom components
        chartInstance.dispatchAction({
          type: 'dataZoom',
          dataZoomIndex: 0,
          start: start,
          end: end
        });

        chartInstance.dispatchAction({
          type: 'dataZoom',
          dataZoomIndex: 1,
          start: start,
          end: end
        });
      }
    },
    [calculateTimeRange, getDateRangeFromSelection, onTimeRangeChange]
  );

  const handleZoomMode = useCallback(() => {
    const next = !isZoomMode;
    setIsZoomMode(next);
    chartRef.current?.getEchartsInstance().dispatchAction({
      type: 'takeGlobalCursor',
      key: 'dataZoomSelect',
      dataZoomSelectActive: next
    });
  }, [isZoomMode]);

  const handleRestoreChart = useCallback(() => {
    setIsZoomMode(false);
    chartRef.current?.getEchartsInstance().dispatchAction({
      type: 'takeGlobalCursor',
      key: 'dataZoomSelect',
      dataZoomSelectActive: false
    });
    handleTimeRangeChange('all');
  }, [handleTimeRangeChange]);

  const handleDownloadChart = useCallback(() => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;
    const dataURL = chart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: isDark ? '#121E2D' : '#fff'
    });
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `${(topLabel || 'chart').trim().replace(/\s+/g, '-')}.png`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [isDark, topLabel]);

  const handleDataZoom = useCallback(
    () => {
      if (chartRef.current) {
        const chartInstance = chartRef.current.getEchartsInstance();
        const dataZoom = chartInstance.getOption()
          .dataZoom as ZoomState[] | undefined;
        const zoomState: ZoomState[] =
          dataZoom?.map((zoom: ZoomState) => ({
            start: zoom.start,
            end: zoom.end
          })) ?? [];
        dataZoomRef.current = zoomState;

        // Only reset to 'all' if user manually zoomed (not programmatic)
        const { start, end } = calculateTimeRange(currentTimeRange.current);
        const currentZoom = zoomState?.[0];

        if (
          currentZoom &&
          ((typeof currentZoom.start === 'number' &&
            Math.abs(currentZoom.start - start) > 1) ||
            (typeof currentZoom.end === 'number' &&
              Math.abs(currentZoom.end - end) > 1))
        ) {
          currentTimeRange.current = 'all';
          setSelectedTimeRange('all');
        }
      }
    },
    [calculateTimeRange]
  );

  const backupLegendState = useCallback(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      const legend = (chartInstance.getOption() as { legend?: unknown[] })
        .legend;
      const legendState = (
        legend?.[0] as { selected?: Record<string, boolean> } | undefined
      )?.selected;
      legendStateRef.current = {
        ...(legendState ?? {})
      };
    }
  }, []);

  const restoreLegendState = useCallback(() => {
    if (chartRef.current && legendStateRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      Object.entries(legendStateRef.current).forEach(([name, isSelected]) => {
        chartInstance.dispatchAction({
          type: 'legendSelect',
          name
        });

        if (!isSelected) {
          chartInstance.dispatchAction({
            type: 'legendUnSelect',
            name
          });
        }
      });
    }
  }, []);

  const formattedDates = (dates ?? []).map(item =>
    isFullDate
      ? timeStampToDate(Number(item)).slice(8)
      : timeStampToDate(Number(item)).slice(8).slice(0, -3)
  );

  const buildChartSeries = useCallback(
    (seriesList = series) =>
      seriesList?.map(
        (
          {
            data,
            name,
            color,
            yAxisIndex = 0,
            axisFormatter,
            inverse,
            axisLabel
          },
          index
        ) => ({
          name,
          type: 'line',
          color,
          ...(independentYAxis
            ? { gridIndex: 0, yAxisIndex: index, xAxisIndex: 0 }
            : dualYAxis
              ? { gridIndex: 0, yAxisIndex, xAxisIndex: 0 }
              : { yAxisIndex: 0, xAxisIndex: 0 }),
          data: data?.map(item => item),
          axisFormatter,
          inverse,
          axisLabel,
          smooth: false,
          labelLayout: { hideOverlap: true, dy: 3, dx: 3 },
          label: {
            fontSize: 12,
            fontFamily: 'dana',
            color: getColor('gray-1000'),
            show: isNumbersAllowed,
            formatter: ({ value }: { value?: ChartValue }) =>
              numberHumanizer(value)?.toString()?.toPersianDigit()
          },
          showSymbol: true,
          symbolSize: isDotsAllowed ? 4 : 0,
          emphasis: {
            lineStyle: {
              width: 3,
              color
            },
            label: {
              show: false
            },
            scale: false
          },
          lineStyle: {
            width: 3,
            color
          },
          itemStyle: {
            color
          }
        })
      ) ?? [],
    [series, dualYAxis, independentYAxis, isDotsAllowed, isNumbersAllowed]
  );

  const chartKey = `${independentYAxis ? 'independent' : dualYAxis ? 'dual' : 'single'}-${series
    ?.map(item => item.name)
    .join('|')}`;

  // Handle dots and numbers toggle without affecting chart zoom
  const handleDotsToggle = useCallback((checked: boolean) => {
    setIsDotsAllowed(checked);
  }, []);

  const handleNumbersToggle = useCallback((checked: boolean) => {
    setIsNumbersAllowed(checked);
  }, []);

  // Only update chart when necessary, preserve zoom state
  useEffect(() => {
    if (chartRef.current && isChartInitialized.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      const dataZoom = chartInstance.getOption()
        .dataZoom as ZoomState[] | undefined;
      // Store current zoom state before update
      const currentZoom = dataZoom?.map((zoom: ZoomState) => ({
        start: zoom.start,
        end: zoom.end
      }));

      // Update series without affecting zoom
      chartInstance.setOption(
        {
          series: buildChartSeries()
        },
        false
      );

      // Restore zoom state
      if (currentZoom && currentZoom.length) {
        currentZoom.forEach((zoom: ZoomState, index: number) => {
          chartInstance.dispatchAction({
            type: 'dataZoom',
            dataZoomIndex: index,
            start: zoom.start,
            end: zoom.end
          });
        });
      }

      restoreLegendState();
    }
  }, [buildChartSeries, restoreLegendState]);

  const { start: initialStart, end: initialEnd } =
    calculateTimeRange(selectedTimeRange);

  if (!dates?.length || !series?.length) {
    return null;
  }

  const initialConfig = ({
    textStyle: {
      fontFamily: 'dana'
    },
    animation: false,
    color: colorList,
    legend: {
      show: false
    },
    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: {
        fontWeight: 500,
        fontSize: 12,
        color: getColor('gray-1100')
      },
      borderWidth: 1,
      transitionDuration: 0,
      position: !isMdUp
        ? (...args: unknown[]) => {
            const pos = args[0] as [number, number];
            const size = args[4] as { viewSize: [number, number] };
            const obj: Record<string, string | number> = { top: '10%' };
            const side = ['left', 'right'][+(pos[0] < size.viewSize[0] / 2)];
            obj[side] = 5;
            return obj;
          }
        : 'auto',
      formatter: (params: TooltipParam[]) => {
        const formattedParams = params
          ?.map(({ seriesName, value, color }) => {
            const marker = `<span style="display:inline-block;margin-left:4px;border-radius:50%;width:10px;height:10px;background-color:${color};"></span>`;

            return `<span class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1" style="direction:ltr;margin:0;padding:0">
            <span class="fw-bolder">${marker} ${showName ? `<span>${seriesName}:  </span>` : ''} </span>
             ${numberHumanizer(value)?.toString()?.toPersianDigit() || 'نامشخص'}
             </span>`;
          })
          ?.join('');
        return `
          <div style="font-family: dana !important;">
          <div class="mb-2 d-flex justify-content-between">
          ${topLabel ? topLabel : params[0]?.seriesName}<span dir="ltr" class="ms-3">${String(params[0]?.axisValue ?? '').toPersianDigit()}</span>
          </div>
          <div style="direction:rtl;" class="d-flex flex-column">
            ${formattedParams}
            </div>
          </div>`;
      }
    },
    axisPointer: {
      link: [
        {
          xAxisIndex: usesSingleGrid ? [0] : [0, 1]
        }
      ]
    },
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: usesSingleGrid ? [0] : [0, 1],
        realtime: false,
        start: initialStart,
        end: initialEnd,
        bottom: 15,
        height: 40,
        handleIcon:
          'path://M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '50%'
      },
      {
        type: 'inside',
        xAxisIndex: usesSingleGrid ? [0] : [0, 1],
        start: initialStart,
        end: initialEnd,
        bottom: 20,
        height: 20
      }
    ],
    xAxis: usesSingleGrid
      ? [
          {
            type: 'category',
            gridIndex: 0,
            data: formattedDates,
            boundaryGap: false,
            axisLine: {
              lineStyle: { color: getColor('gray-300') }
            },
            axisLabel: {
              margin: 15,
              color: getColor('gray-600')
            },
            min: 'dataMin',
            max: 'dataMax',
            axisPointer: {
              show: true
            }
          }
        ]
      : [
          {
            type: 'category',
            data: formattedDates,
            boundaryGap: false,
            axisLine: {
              lineStyle: { color: getColor('gray-300') }
            },
            axisLabel: {
              margin: 15,
              color: getColor('gray-600')
            },
            min: 'dataMin',
            max: 'dataMax',
            axisPointer: {
              show: true
            }
          },
          {
            type: 'category',
            gridIndex: 1,
            data: dates,
            scale: true,
            boundaryGap: false,
            splitLine: { show: false },
            axisLabel: { show: false },
            axisTick: { show: false },
            axisLine: {
              show: false
            },
            splitNumber: 20,
            min: 'dataMin',
            max: 'dataMax',
            axisPointer: {
              type: 'shadow',
              label: { show: false },
              triggerTooltip: true
            }
          }
        ],
    yAxis: independentYAxis
      ? buildIndependentYAxes(series, isInversed)
      : dualYAxis
        ? [
          {
            type: 'value',
            gridIndex: 0,
            inverse: isInversed,
            scale: true,
            position: 'left',
            name: leftAxisLabel,
            alignTicks: true,
            nameTextStyle: {
              color: getColor('gray-600'),
              fontFamily: 'dana'
            },
            axisLine: { show: false, onZero: false },
            splitLine: {
              lineStyle: {
                color: getColor('gray-200')
              }
            },
            axisTick: { show: false },
            axisLabel: {
              color: getColor('gray-600')
            }
          },
          {
            type: 'value',
            gridIndex: 0,
            scale: true,
            position: 'right',
            name: rightAxisLabel,
            alignTicks: true,
            nameTextStyle: {
              color: getColor('gray-600'),
              fontFamily: 'dana'
            },
            axisLine: { show: false, onZero: false },
            splitLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              color: getColor('gray-600')
            }
          }
        ]
        : [
          {
            type: 'value',
            inverse: isInversed,
            scale: true,
            axisLine: { show: false },
            splitLine: {
              lineStyle: {
                color: getColor('gray-200')
              }
            },
            axisTick: { show: false },
            axisLabel: {
              color: getColor('gray-600')
            }
          },
          {
            type: 'value',
            scale: true,
            gridIndex: 1,
            splitNumber: 2,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false }
          }
        ],
    grid: usesSingleGrid
      ? [
          {
            left: 24,
            right: 24,
            bottom: 60,
            containLabel: true
          }
        ]
      : [
          {
            left: 20,
            right: 20,
            bottom: 60,
            containLabel: true
          },
          {
            left: 50,
            right: 20,
            height: 40,
            top: 260,
            containLabel: true
          }
        ],
    toolbox: {
      show: false,
      feature: {
        dataZoom: {
          yAxisIndex: 'none'
        }
      }
    },
    series: buildChartSeries()
  } as unknown) as EChartsOption;

  // // Calculate time range start and end percentages
  // const calculateTimeRange = useCallback(
  //   range => {
  //     if (range === 'all' || !dates || dates.length === 0) {
  //       return { start: 0, end: 100 };
  //     }

  //     const totalDays = dates.length;
  //     const rangeDays = timeRangeOptions.find(
  //       option => option.value === range
  //     )?.days;

  //     if (!rangeDays || rangeDays >= totalDays) {
  //       return { start: 0, end: 100 };
  //     }

  //     const startPercentage = Math.max(
  //       0,
  //       ((totalDays - rangeDays) / totalDays) * 100
  //     );
  //     return { start: startPercentage, end: 100 };
  //   },
  //   [dates]
  // );

  // // Handle time range change without re-rendering chart
  // const handleTimeRangeChange = useCallback(
  //   range => {
  //     currentTimeRange.current = range;
  //     setSelectedTimeRange(range);

  //     if (chartRef.current && isChartInitialized.current) {
  //       const chartInstance = chartRef.current.getEchartsInstance();
  //       const { start, end } = calculateTimeRange(range);

  //       // Apply the time range to dataZoom components
  //       chartInstance.dispatchAction({
  //         type: 'dataZoom',
  //         dataZoomIndex: 0,
  //         start: start,
  //         end: end
  //       });

  //       chartInstance.dispatchAction({
  //         type: 'dataZoom',
  //         dataZoomIndex: 1,
  //         start: start,
  //         end: end
  //       });
  //     }
  //   },
  //   [calculateTimeRange]
  // );

  // const handleDataZoom = useCallback(
  //   params => {
  //     if (chartRef.current && isChartInitialized.current) {
  //       const chartInstance = chartRef.current.getEchartsInstance();
  //       const zoomState = chartInstance.getOption().dataZoom?.map(zoom => ({
  //         start: zoom.start,
  //         end: zoom.end
  //       }));
  //       dataZoomRef.current = zoomState;

  //       // Only reset to 'all' if user manually zoomed (not programmatic)
  //       const { start, end } = calculateTimeRange(currentTimeRange.current);
  //       const currentZoom = zoomState?.[0];

  //       if (
  //         currentZoom &&
  //         currentTimeRange.current !== 'all' &&
  //         (Math.abs(currentZoom.start - start) > 2 ||
  //           Math.abs(currentZoom.end - end) > 2)
  //       ) {
  //         currentTimeRange.current = 'all';
  //         setSelectedTimeRange('all');
  //       }
  //     }
  //   },
  //   [calculateTimeRange]
  // );

  // const backupLegendState = useCallback(() => {
  //   if (chartRef.current && isChartInitialized.current) {
  //     const chartInstance = chartRef.current.getEchartsInstance();
  //     const legendState = chartInstance.getOption().legend[0].selected;
  //     legendStateRef.current = { ...legendState };
  //   }
  // }, []);

  // const restoreLegendState = useCallback(() => {
  //   if (
  //     chartRef.current &&
  //     legendStateRef.current &&
  //     isChartInitialized.current
  //   ) {
  //     const chartInstance = chartRef.current.getEchartsInstance();
  //     Object.entries(legendStateRef.current).forEach(([name, isSelected]) => {
  //       chartInstance.dispatchAction({
  //         type: 'legendSelect',
  //         name
  //       });

  //       if (!isSelected) {
  //         chartInstance.dispatchAction({
  //           type: 'legendUnSelect',
  //           name
  //         });
  //       }
  //     });
  //   }
  // }, []);

  // // Handle dots toggle without re-rendering
  // const handleDotsToggle = useCallback(
  //   checked => {
  //     setIsDotsAllowed(checked);

  //     if (chartRef.current && isChartInitialized.current) {
  //       const chartInstance = chartRef.current.getEchartsInstance();

  //       chartInstance.setOption(
  //         {
  //           series: series.map(ser => ({
  //             symbolSize: checked ? 4 : 0
  //           }))
  //         },
  //         false
  //       );
  //     }
  //   },
  //   [series]
  // );

  // // Handle numbers toggle without re-rendering
  // const handleNumbersToggle = useCallback(
  //   checked => {
  //     setIsNumbersAllowed(checked);

  //     if (chartRef.current && isChartInitialized.current) {
  //       const chartInstance = chartRef.current.getEchartsInstance();

  //       chartInstance.setOption(
  //         {
  //           series: series.map(ser => ({
  //             label: {
  //               show: checked
  //             }
  //           }))
  //         },
  //         false
  //       );
  //     }
  //   },
  //   [series]
  // );

  // // Mark chart as initialized after first render
  // useEffect(() => {
  //   if (chartRef.current && !isChartInitialized.current) {
  //     setTimeout(() => {
  //       isChartInitialized.current = true;
  //     }, 100);
  //   }
  // }, []);

  // const formattedDates = dates.map(item =>
  //   isFullDate
  //     ? timeStampToDate(Number(item)).slice(8)
  //     : timeStampToDate(Number(item)).slice(8).slice(0, -3)
  // );

  return (
    <>
      {!hideControls && (
        <div className="d-flex gap-2 flex-wrap align-items-center justify-content-between w-100">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <h6 className="fs--1 mb-0">نمایش نقطه </h6>
              <Form.Check
                type="switch"
                id="dots-switch"
                checked={isDotsAllowed}
                onChange={({ target }) => handleDotsToggle(target.checked)}
              />
            </div>
            <div className="d-flex gap-2 align-items-center">
              <h6 className="fs--1 mb-0">نمایش عدد </h6>
              <Form.Check
                type="switch"
                id="numbers-switch"
                checked={isNumbersAllowed}
                onChange={({ target }) => handleNumbersToggle(target.checked)}
              />
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2" dir="ltr">
            <Dropdown
              align="start"
              onSelect={value => {
                if (value) handleTimeRangeChange(value as TimeRangeValue);
              }}
            >
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                style={{ fontSize: '12px', padding: '4px 12px' }}
              >
                {timeRangeOptions.find(
                  option => option.value === selectedTimeRange
                )?.label}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {timeRangeOptions.map(option => (
                  <Dropdown.Item
                    key={option.value}
                    eventKey={option.value}
                    active={selectedTimeRange === option.value}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <h6 className="fs--1 mb-0 ms-3">بازه زمانی</h6>
            <div className="d-flex align-items-center gap-2 ms-5">
              {(
                [
                  {
                    label: 'بزرگ‌نمایی',
                    icon: 'search-plus',
                    onClick: handleZoomMode,
                    active: isZoomMode
                  },
                  {
                    label: 'بازنشانی',
                    icon: 'undo-alt',
                    onClick: handleRestoreChart
                  },
                  {
                    label: 'دانلود',
                    icon: 'download',
                    onClick: handleDownloadChart
                  }
                ] as const
              ).map(action => (
                <OverlayTrigger
                  key={action.label}
                  placement="top"
                  overlay={<Tooltip>{action.label}</Tooltip>}
                >
                  <button
                    type="button"
                    aria-label={action.label}
                    className={`btn btn-sm d-flex align-items-center justify-content-center ${
                      'active' in action && action.active
                        ? 'btn-falcon-primary'
                        : 'btn-outline-secondary'
                    }`}
                    onClick={action.onClick}
                    style={{ width: 32, height: 28, padding: 0 }}
                  >
                    <FontAwesomeIcon icon={action.icon} />
                  </button>
                </OverlayTrigger>
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        className={
          hideControls ? 'bg-100 rounded-2' : 'mt-3 bg-100'
        }
      >
        <ReactEChartsCore
          key={chartKey}
          echarts={echarts}
          ref={chartRef}
          onEvents={{
            dataZoom: handleDataZoom,
            legendselectchanged: backupLegendState
          }}
          option={initialConfig}
          notMerge
          style={{ height: '24.75rem' }}
        />
        {showLegent && (
          <div className="d-flex flex-wrap gap-2 justify-content-center pb-3 pt-1 align-items-center">
            {series.map(item => (
              <CustomLegent
                chartRef={chartRef}
                name={item.name}
                color={item.itemStyle?.color ?? item.color ?? getColor('gray-600')}
                key={item.name}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default LineTimeChart;
